import * as React from "react";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import type {
    EloshieldClassicStationaryModuleCreate,
    EloshieldClassicStationaryModule,
} from "../../api";
import {
    doubleBindIntegerToString,
    doubleBindNullableStringEmptyAsNull,
    doubleBindString,
} from "../../utils/double-bind";
import type { FormCreateProps } from "../../utils/form-create-props";
import { createUuid } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { store } from "../../store";
import type { SyntheticEvent } from "react";

export interface FormCreateEloshieldClassicStationaryModuleProps
    extends FormCreateProps<EloshieldClassicStationaryModule> {}

@observer
export class FormCreateEloshieldClassicStationaryModule extends React.Component<FormCreateEloshieldClassicStationaryModuleProps> {
    @observable public accessor serialNumber = "";
    @observable public accessor label = "";
    @observable public accessor antennaACount = 0;
    @observable public accessor antennaBCount = 0;

    private validationId = createUuid();

    constructor(props: FormCreateEloshieldClassicStationaryModuleProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryEloshieldClassicStationaryModules.validation.create.updateModel(
            this.validationId,
            this.eloshieldClassicStationaryModuleCreate,
        );
    }

    componentDidMount(): void {
        store.repositoryEloshieldClassicStationaryModules.validation.create.initializeModel(
            this.validationId,
            this.eloshieldClassicStationaryModuleCreate,
        );
    }

    @computed
    private get eloshieldClassicStationaryModuleCreate(): EloshieldClassicStationaryModuleCreate {
        const { label, serialNumber, antennaACount, antennaBCount } = this;
        return { label, serialNumber, antennaACount, antennaBCount };
    }

    @action.bound
    private async createEloshieldClassicStationaryModule(
        evt: SyntheticEvent<HTMLFormElement>,
    ): Promise<void> {
        evt.preventDefault();
        const eloshieldClassicStationaryModule =
            await store.repositoryEloshieldClassicStationaryModules.create(
                this.eloshieldClassicStationaryModuleCreate,
            );
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(eloshieldClassicStationaryModule);
        }
    }

    @action.bound private reset(): void {
        this.label = "";
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateEloshieldClassicStationaryModule.submit.text")}
                validation={store.repositoryEloshieldClassicStationaryModules.validation.create}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.createEloshieldClassicStationaryModule}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t(
                            "formCreateEloshieldClassicStationaryModule.label.label",
                        )}
                        {...doubleBindNullableStringEmptyAsNull<FormCreateEloshieldClassicStationaryModule>(
                            this,
                            "label",
                            () => this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryEloshieldClassicStationaryModules.validation.create.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t(
                            "formCreateEloshieldClassicStationaryModule.serialNumber.label",
                        )}
                        {...doubleBindString<FormCreateEloshieldClassicStationaryModule>(
                            this,
                            "serialNumber",
                            () => this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryEloshieldClassicStationaryModules.validation.create.getFieldValidationState(
                                this.validationId,
                                "serialNumber",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t(
                            "formCreateEloshieldClassicStationaryModule.antennaACount.label",
                        )}
                        type="number"
                        min={0}
                        max={4}
                        {...doubleBindIntegerToString<FormCreateEloshieldClassicStationaryModule>(
                            this,
                            "antennaACount",
                            () => this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryEloshieldClassicStationaryModules.validation.create.getFieldValidationState(
                                this.validationId,
                                "antennaACount",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t(
                            "formCreateEloshieldClassicStationaryModule.antennaBCount.label",
                        )}
                        type="number"
                        min={0}
                        max={4}
                        {...doubleBindIntegerToString<FormCreateEloshieldClassicStationaryModule>(
                            this,
                            "antennaBCount",
                            () => this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryEloshieldClassicStationaryModules.validation.create.getFieldValidationState(
                                this.validationId,
                                "antennaBCount",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t(
                                "formCreateEloshieldClassicStationaryModule.cancel.text",
                            )}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
